import React, { useEffect, useRef } from 'react';

interface AssetGaugeProps {
  value: number;
  min: number;
  max: number;
  unit: string;
  title: string;
}

export const AssetGauge: React.FC<AssetGaugeProps> = ({ value, min, max, unit, title }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const percentage = ((value - min) / (max - min)) * 100;
  const angle = (percentage * 180) / 100;
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up high DPI canvas
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const radius = Math.min(centerX, centerY) - 20;

    // Draw 3D outer ring
    const gradient = ctx.createLinearGradient(0, 0, 0, rect.height);
    gradient.addColorStop(0, '#2563eb'); // Blue-600
    gradient.addColorStop(1, '#1d4ed8'); // Blue-700

    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, Math.PI, 0);
    ctx.lineWidth = 20;
    ctx.strokeStyle = gradient;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Add 3D shadow effect
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 5;

    // Draw background track
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, Math.PI, 0);
    ctx.lineWidth = 15;
    ctx.strokeStyle = '#f3f4f6'; // Gray-100
    ctx.stroke();

    // Draw value arc
    const valueAngle = Math.PI + (Math.PI * percentage) / 100;
    const valueGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    
    if (percentage <= 33) {
      valueGradient.addColorStop(0, '#22c55e'); // Green-600
      valueGradient.addColorStop(1, '#16a34a'); // Green-700
    } else if (percentage <= 66) {
      valueGradient.addColorStop(0, '#eab308'); // Yellow-600
      valueGradient.addColorStop(1, '#ca8a04'); // Yellow-700
    } else {
      valueGradient.addColorStop(0, '#ef4444'); // Red-600
      valueGradient.addColorStop(1, '#dc2626'); // Red-700
    }

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, Math.PI, valueAngle);
    ctx.lineWidth = 15;
    ctx.strokeStyle = valueGradient;
    ctx.stroke();

    // Add metallic shine effect
    const shineGradient = ctx.createLinearGradient(0, 0, 0, rect.height);
    shineGradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
    shineGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
    shineGradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)');

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, Math.PI, valueAngle);
    ctx.lineWidth = 15;
    ctx.strokeStyle = shineGradient;
    ctx.stroke();

    // Draw tick marks
    for (let i = 0; i <= 10; i++) {
      const tickAngle = Math.PI + (Math.PI * i) / 10;
      const tickLength = i % 5 === 0 ? 10 : 5;
      const startRadius = radius - 25;
      const endRadius = startRadius - tickLength;

      ctx.beginPath();
      ctx.moveTo(
        centerX + Math.cos(tickAngle) * startRadius,
        centerY + Math.sin(tickAngle) * startRadius
      );
      ctx.lineTo(
        centerX + Math.cos(tickAngle) * endRadius,
        centerY + Math.sin(tickAngle) * endRadius
      );
      ctx.lineWidth = i % 5 === 0 ? 2 : 1;
      ctx.strokeStyle = '#6b7280'; // Gray-500
      ctx.stroke();
    }

    ctx.restore();

  }, [value, percentage]);

  return (
    <div className="relative w-full">
      <div className="text-center mb-2">
        <h3 className="text-sm font-medium text-gray-600 truncate">{title}</h3>
      </div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-40"
          style={{ 
            filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))',
            transform: 'translateZ(0)',
            animation: 'gauge-fade-in 1s ease-out'
          }}
        />
        <div 
          className="absolute bottom-8 left-0 right-0 text-center"
          style={{ animation: 'value-fade-in 1s ease-out' }}
        >
          <div className="text-2xl font-bold text-gray-900">
            {value.toFixed(1)}
          </div>
          <div className="text-sm text-gray-500">
            {unit}
          </div>
        </div>
      </div>
      <style>
        {`
          @keyframes gauge-fade-in {
            from {
              opacity: 0;
              transform: translateY(20px) translateZ(0);
            }
            to {
              opacity: 1;
              transform: translateY(0) translateZ(0);
            }
          }

          @keyframes value-fade-in {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          canvas {
            backface-visibility: hidden;
            perspective: 1000;
            transform-style: preserve-3d;
          }
        `}
      </style>
    </div>
  );
};